import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material'
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import biomsa from 'biomsa'
import cloneDeep from 'lodash/cloneDeep'
import { addAlert } from '../../actions/alerts'



function preserveOriginalCaseAlignment(originalSeqs, alignedSeqs) {
    return alignedSeqs.map((aligned, idx) => {
        const original = originalSeqs[idx].sequences;
        let origIndex = 0;

        return aligned.split('').map(char => {
            if (char === '-') return '-'; // Keep gap
            const origChar = original[origIndex++];
            return origChar || char; // Preserve original case
        }).join('');
    });
}

function getConsensus(sequences) {
    if (sequences.length === 0) return '';

    const maxLen = Math.max(...sequences.map(seq => seq.length));
    let consensus = '';

    for (let i = 0; i < maxLen; i++) {
        const charsAtPos = sequences.map(seq => seq[i] || '');
        const freqMap = {};

        charsAtPos.forEach(ch => {
            freqMap[ch] = (freqMap[ch] || 0) + 1;
        });

        // Get most frequent char at this position
        let maxChar = '';
        let maxCount = 0;
        for (const ch in freqMap) {
            if (freqMap[ch] > maxCount) {
                maxCount = freqMap[ch];
                maxChar = ch;
            }
        }
        consensus += maxChar;
    }

    return consensus;
}

const handleAlign = async (seqs, setSeqs, setConsensus) => {
    try {
        const clonedSeqs = cloneDeep(seqs);
        const result = await biomsa.align(clonedSeqs.map(y => y.sequences));
        const casedAligned = preserveOriginalCaseAlignment(clonedSeqs, result);

        for (let i = 0; i < casedAligned.length; i++) {
            clonedSeqs[i].sequences = casedAligned[i];
        }

        setSeqs(clonedSeqs);
        const consensusStr = getConsensus(clonedSeqs.map(s => s.sequences));
        setConsensus(consensusStr);

    } catch (e) {
        addAlert({ message: e });
    }
};



const handleExport = async (x) => {
    const tableData = x.map(y => y.sequences);

    const workbook = new ExcelJS.Workbook();
    const sheet1 = workbook.addWorksheet('Full seq');

    // Apply Consolas font
    tableData.forEach((value, index) => {
        sheet1.getCell(`A${index + 1}`).value = value;
        sheet1.getCell(`A${index + 1}`).font = { name: 'Consolas' };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "Sequence_alignment.xlsx");
};


const SequenceAlignModal = ({ open, onClose, sequences }) => {
    const [seqs, setSeqs] = useState([]);
    const originalSeqsRef = useRef([]);
    const scrollRefs = useRef([]);

    useEffect(() => {
        if (open) {
            const cloned = cloneDeep(sequences);
            setSeqs(cloned);
            originalSeqsRef.current = cloneDeep(cloned);
        } else {
            setSeqs([]);
            originalSeqsRef.current = [];
        }
    }, [open]);

    // Sync scrollLeft across all textfields
    const syncScroll = (sourceIndex) => (e) => {
        const scrollLeft = e.target.scrollLeft;
        scrollRefs.current.forEach((ref, i) => {
            if (i !== sourceIndex && ref) {
                ref.scrollLeft = scrollLeft;
            }
        });
    };

    // Get consensus string from sequences
    const consensus = useMemo(() => {
        const allSequences = seqs.map((s) => s.sequences);
        if (allSequences.length === 0) return '';
        const maxLen = Math.max(...allSequences.map((seq) => seq.length));
        let result = '';
        for (let i = 0; i < maxLen; i++) {
            const chars = allSequences.map((seq) => seq[i] || '');
            const freqMap = {};
            chars.forEach((ch) => {
                freqMap[ch] = (freqMap[ch] || 0) + 1;
            });
            let maxChar = '';
            let maxCount = 0;
            for (const ch in freqMap) {
                if (freqMap[ch] > maxCount) {
                    maxCount = freqMap[ch];
                    maxChar = ch;
                }
            }
            result += maxChar;
        }
        return result;
    }, [seqs]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '90vw',
                    height: '70vh',
                },
            }}
        >
            <DialogTitle>Sequence Alignment</DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
                {/* Header Row */}
                <Box display="flex" alignItems="center" mb={1} fontWeight="bold">
                    <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center' }}>V</Box>
                    <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center' }}>D</Box>
                    <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center' }}>J</Box>
                    <Box flexGrow={1}>Sequence</Box>
                </Box>

                {/* Sequence Rows */}
                {seqs.map((seq, i) => (
                    <Box
                        key={i}
                        display="flex"
                        alignItems="flex-start"
                        mb={2}
                        sx={{ fontFamily: 'monospace', fontSize: '14px', position: 'relative' }}
                    >
                        <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center', fontSize: 'small' }}>
                            {seq.V}
                        </Box>
                        <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center', fontSize: 'small' }}>
                            {seq.D}
                        </Box>
                        <Box sx={{ width: 40, minWidth: 40, maxWidth: 40, mr: 3, textAlign: 'center', fontSize: 'small' }}>
                            {seq.J}
                        </Box>

                        {/* Editable Sequence Field with Overlay */}
                        <Box
                            sx={{
                                flexGrow: 1,
                                position: 'relative',
                                overflowX: 'auto',    // ← only this scrolls horizontally
                                overflowY: 'hidden',
                                border: '1px solid #ccc',
                                borderRadius: 1,
                                height: '45px',
                                whiteSpace: 'nowrap',  // prevent any wrapping
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                lineHeight: '32px',
                            }}
                            ref={(el) => (scrollRefs.current[i] = el)}
                            onScroll={syncScroll(i)}
                        >
                            {/* Highlight Layer */}
                            <Box
                                aria-hidden
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    whiteSpace: 'pre',
                                    pointerEvents: 'none',
                                    userSelect: 'none',
                                    zIndex: 1,
                                    display: 'inline-block',
                                    paddingLeft: '0px',
                                }}
                            >
                                {seq.sequences.split('').map((char, idx) => {
                                    const isMismatch = consensus[idx] && char !== consensus[idx];
                                    return (
                                        <span
                                            key={idx}
                                            style={{
                                                backgroundColor: isMismatch ? '#ffcccb' : 'transparent',
                                                display: 'inline-block',
                                                width: '1ch',
                                            }}
                                        >
                                            {char}
                                        </span>
                                    );
                                })}
                            </Box>

                            {/* Transparent Textarea Layer */}
                            <textarea
                                spellCheck={false}
                                value={seq.sequences}
                                onChange={(e) => {
                                    const newSeqs = [...seqs];
                                    newSeqs[i].sequences = e.target.value;
                                    setSeqs(newSeqs);
                                }}
                                style={{
                                    position: 'relative',
                                    background: 'transparent',
                                    color: 'transparent',
                                    caretColor: 'black',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    lineHeight: '32px',
                                    border: 'none',
                                    resize: 'none',
                                    display: 'inline-block',   // flow with highlight layer
                                    width: `${seq.sequences.length}ch`,  // ensure it’s as wide as its content
                                    height: '100%',
                                    overflow: 'hidden',        // no inner scroll
                                    whiteSpace: 'pre',         // preserve spaces
                                    paddingLeft: '0px',
                                    margin: 0,
                                    outline: 'none',
                                    zIndex: 2,
                                }}
                            />
                        </Box>

                    </Box>
                ))}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => handleAlign(seqs, setSeqs)}>Align</Button>
                <Button onClick={() => setSeqs(cloneDeep(originalSeqsRef.current))}>Clear changes</Button>
                <Button onClick={() => handleExport(seqs)}>Export</Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default SequenceAlignModal;
